import { apiGet, apiPost, apiPut, apiDelete } from './apiUtils';
import {
  UserProtocol,
  CreateProtocolRequest,
  UpdateProtocolRequest,
  ProtocolFilters,
  ProtocolListResponse,
} from '../types/protocols';

// Получение списка протоколов пользователя
export const getUserProtocols = async (filters: ProtocolFilters = {}): Promise<ProtocolListResponse> => {
  const params = new URLSearchParams();

  if (filters.specialty) params.append('specialty', filters.specialty);
  if (filters.status && filters.status !== 'all') params.append('active_only', filters.status === 'active' ? 'true' : 'false');
  if (filters.search) params.append('query', filters.search);
  if (filters.skip !== undefined) params.append('skip', filters.skip.toString());
  if (filters.limit !== undefined) params.append('limit', filters.limit.toString());

  const queryString = params.toString();
  const url = `/protocols${queryString ? `?${queryString}` : ''}`;

  const response = await apiGet(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch protocols: ${response.status}`);
  }

  return response.json();
};

// Поиск протоколов
export const searchProtocols = async (query: string, filters: Omit<ProtocolFilters, 'search'> = {}): Promise<ProtocolListResponse> => {
  const params = new URLSearchParams();
  params.append('query', query);

  if (filters.specialty) params.append('specialty', filters.specialty);
  if (filters.status && filters.status !== 'all') params.append('active_only', filters.status === 'active' ? 'true' : 'false');
  if (filters.skip !== undefined) params.append('skip', filters.skip.toString());
  if (filters.limit !== undefined) params.append('limit', filters.limit.toString());

  const url = `/protocols/search?${params.toString()}`;
  const response = await apiGet(url);

  if (!response.ok) {
    throw new Error(`Failed to search protocols: ${response.status}`);
  }

  return response.json();
};

// Получение протокола по ID
export const getProtocolById = async (id: number): Promise<UserProtocol> => {
  const response = await apiGet(`/protocols/${id}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch protocol: ${response.status}`);
  }

  return response.json();
};

// Создание нового протокола
export const createProtocol = async (protocol: CreateProtocolRequest): Promise<UserProtocol> => {
  const response = await apiPost('/protocols/', protocol);

  if (!response.ok) {
    throw new Error(`Failed to create protocol: ${response.status}`);
  }

  return response.json();
};

// Обновление протокола
export const updateProtocol = async (id: number, protocol: UpdateProtocolRequest): Promise<UserProtocol> => {
  console.log('API updateProtocol - sending data:', { id, protocol });
  const response = await apiPut(`/protocols/${id}`, protocol);

  if (!response.ok) {
    throw new Error(`Failed to update protocol: ${response.status}`);
  }

  const result = await response.json();
  console.log('API updateProtocol - received response:', result);
  return result;
};

// Удаление протокола
export const deleteProtocol = async (id: number): Promise<void> => {
  const response = await apiDelete(`/protocols/${id}`);

  if (!response.ok) {
    throw new Error(`Failed to delete protocol: ${response.status}`);
  }
};

// Активация протокола
export const activateProtocol = async (id: number): Promise<UserProtocol> => {
  const response = await apiPost(`/protocols/${id}/activate`, {});

  if (!response.ok) {
    throw new Error(`Failed to activate protocol: ${response.status}`);
  }

  return response.json();
};

// Деактивация протокола
export const deactivateProtocol = async (id: number): Promise<UserProtocol> => {
  const response = await apiPost(`/protocols/${id}/deactivate`, {});

  if (!response.ok) {
    throw new Error(`Failed to deactivate protocol: ${response.status}`);
  }

  return response.json();
};

// Админ API для получения всех протоколов
export const getAllProtocols = async (filters: ProtocolFilters = {}): Promise<ProtocolListResponse> => {
  const params = new URLSearchParams();

  // Для админов добавляем параметр all=true
  params.append('all', 'true');

  if (filters.specialty) params.append('specialty', filters.specialty);
  if (filters.status && filters.status !== 'all') params.append('active_only', filters.status === 'active' ? 'true' : 'false');
  if (filters.search) params.append('query', filters.search);
  if (filters.skip !== undefined) params.append('skip', filters.skip.toString());
  if (filters.limit !== undefined) params.append('limit', filters.limit.toString());

  const queryString = params.toString();
  const url = `/protocols?${queryString}`;

  const response = await apiGet(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch all protocols: ${response.status}`);
  }

  return response.json();
};

// Админ API для получения протокола по ID (использует общий эндпоинт)
export const getProtocolByIdAdmin = async (id: number): Promise<UserProtocol> => {
  return getProtocolById(id);
};

// Админ API для обновления протокола (использует общий эндпоинт)
export const updateProtocolAdmin = async (id: number, protocol: UpdateProtocolRequest): Promise<UserProtocol> => {
  return updateProtocol(id, protocol);
};

// Админ API для удаления протокола (использует общий эндпоинт)
export const deleteProtocolAdmin = async (id: number): Promise<void> => {
  return deleteProtocol(id);
};

